import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleLogo from '../Assets/Google__G__logo.svg.webp';
import LoadingSpinner from './LoadingSpinner';
import { AUTH_BASE } from '../config';

/**
 * Login Component - Handles user authentication via Google OAuth with UMass email validation
 * This component provides a Google Sign-in button and manages the authentication state
 */
function Login() {
  // State to store any error messages that might occur during authentication
  const [error, setError] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  // useEffect hook runs when the component mounts
  useEffect(() => {
    checkAuthStatus();
  }, [navigate]);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${AUTH_BASE}/status`, {
        credentials: 'include'  // Important: This allows the cookie to be sent with the request
      });
      const data = await response.json();
      
      if (data.isAuthenticated && data.user) {
        navigate('/dashboard');
      } else if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setError('Failed to check authentication status');
    } finally {
      setIsChecking(false);
    }
  };

  /**
   * Handler for Google login button click
   * Redirects user to Google OAuth flow
   */
  const handleGoogleLogin = () => {
    window.location.href = `${AUTH_BASE}/google`;
  };

  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--surface-bg)',
        color: 'var(--text-strong)',
        fontFamily: 'var(--font)'
      }}>
        <div style={{ textAlign:'center' }}>
          <LoadingSpinner size={60}/>
          <div style={{ marginTop:'1rem', color:'var(--maroon)', fontWeight: 600 }}>Checking authentication status...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--surface-bg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font)',
      padding: '2rem'
    }}>
      {/* Header with UTrade branding */}
      <div style={{
        textAlign: 'center',
        marginBottom: '2.5rem'
      }}>
        <h1 style={{ 
          fontFamily: 'var(--font-heading)',
          fontSize: '3rem',
          fontWeight: '700',
          margin: '0 0 0.5rem 0',
          color: 'var(--maroon)',
          letterSpacing: '-1px'
        }}>
          UTrade
        </h1>
        <p style={{ 
          fontSize: '1.15rem',
          margin: '0',
          color: 'var(--text-soft)'
        }}>
          Community Marketplace Login
        </p>
      </div>

      {/* Main login card */}
      <div style={{
        backgroundColor: 'var(--surface-card)',
        padding: '3rem',
        borderRadius: '20px',
        border: '1px solid var(--border-subtle)',
        boxShadow: '0 10px 30px rgba(32,32,32,0.08)',
        textAlign: 'center',
        maxWidth: '450px',
        width: '100%'
      }}>
        <h2 style={{
          margin: '0 0 1rem 0',
          fontSize: '1.5rem',
          fontWeight: '600',
          color: 'var(--maroon)'
        }}>
          Sign In to Your Account
        </h2>

        <p style={{
          margin: '0 0 2rem 0',
          fontSize: '1rem',
          color: 'var(--text-soft)',
          lineHeight: '1.5'
        }}>
          Use your UMass email address to access the community marketplace
        </p>

        {/* Error message display - friendly card shown if sign-in was rejected */}
        {error && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.25rem',
            backgroundColor: 'var(--maroon-light)',
            borderRadius: '12px',
            border: '1px solid var(--maroon-border)',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--maroon)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12" y2="16" />
              </svg>
              <strong style={{ color: 'var(--maroon)', fontSize: '1rem' }}>UMass email required</strong>
            </div>
            <p style={{ margin: '0 0 0.4rem 0', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {error}
            </p>
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.5 }}>
              Tap <strong>Continue with Google</strong> below and choose your <strong>@umass.edu</strong> account.
            </p>
          </div>
        )}

        {/* Google Sign-in Button */}
        <button
          onClick={handleGoogleLogin}
          style={{
            padding: '16px 24px',
            fontSize: '1.1rem',
            fontWeight: '600',
            backgroundColor: '#ffffff',
            color: 'var(--maroon)',
            border: '2px solid var(--maroon)',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(136, 28, 28, 0.15)'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f5f5f5';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 25px rgba(136, 28, 28, 0.25)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(136, 28, 28, 0.15)';
          }}
        >
          {/* Google Logo */}
          <img 
            src={GoogleLogo}
            alt="Google Logo"
            style={{ width: '20px', height: '20px' }}
          />
          Continue with Google
        </button>

        {/* UMass email requirement notice */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: 'var(--maroon-light)',
          borderRadius: '10px',
          border: '1px solid var(--maroon-border)'
        }}>
          <div style={{
            color: 'var(--maroon)',
            fontWeight: '600',
            marginBottom: '0.5rem',
            fontSize: '0.95rem'
          }}>
            UMass Email Required
          </div>
          <p style={{
            margin: '0',
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
            lineHeight: '1.4'
          }}>
            You must sign in with a <strong>@umass.edu</strong> email address to access UTrade. 
            Other email domains will not be accepted.
          </p>
        </div>

        {/* Additional info */}
        <div style={{
          marginTop: '1.5rem',
          fontSize: '0.85rem',
          color: 'var(--text-soft)',
          lineHeight: '1.4'
        }}>
          New to UTrade? Your account will be created automatically when you sign in with your UMass email.
        </div>
      </div>

      {/* Back to home link */}
      <button
        onClick={() => navigate('/')}
        style={{
          marginTop: '2rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: 'transparent',
          color: 'var(--maroon)',
          border: '1px solid var(--maroon-border)',
          borderRadius: '50px',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontFamily: 'var(--font-heading)',
          fontWeight: 600,
          transition: 'all 0.3s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--maroon-light)';
          e.currentTarget.style.borderColor = 'var(--maroon)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = 'var(--maroon-border)';
        }}
      >
        ← Back to Home
      </button>
    </div>
  );
}

export default Login; 